import React, { useState, useEffect, useRef } from "react";
import { Header } from "../../components/SpearParts/Layouts/Header";
import { Link, useParams } from "react-router-dom";
import { Footer } from "../../components/SpearParts/Layouts/Footer";
import Axios from "../../axios/axios";
import { toast } from "react-toastify";
import { toTitleCase } from "../../components/utils/StringUtil";
import html2pdf from "html2pdf.js";
import TopNavBar from "../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../components/SpearParts/Layouts/TopSearchBar";

function PurchaseOrder() {
  const [orders, setOrders] = useState([]);
  const [customer, setCustomer] = useState(null);

  const poRef = useRef();

  const { id } = useParams();
  const [loadingId, setLoadingId] = useState(null); // Track which order is being updated

  useEffect(() => {
    fetchCompletedOrders();
  }, []);

  const fetchCompletedOrders = async () => {
    const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
    const customerId = userInfo?.customerId;
    const businessType = sessionStorage.getItem("businessType")?.toLowerCase();

    if (!customerId || !businessType) return;

    try {
      const [approvedOrdersRes, customerRes] = await Promise.all([
        Axios.get(`/order/approved/${customerId}`),
        Axios.get(`/customer/get/${customerId}`),
      ]);

      // Handle case where response might be an object with data array
      const allOrders = Array.isArray(approvedOrdersRes.data)
        ? approvedOrdersRes.data
        : approvedOrdersRes.data?.orders || approvedOrdersRes.data?.data || [];

      console.log("All approved orders:", allOrders);

      if (!Array.isArray(allOrders)) {
        console.error("Expected an array of orders but got:", allOrders);
        return;
      }

      const filteredOrders = allOrders.filter((order) => {
        if (!order.orderItems || !Array.isArray(order.orderItems)) return false;

        return order.orderItems.some((item) => {
          const productTypes = Array.isArray(item.productType)
            ? item.productType
            : [item.productType];

          return productTypes.some(
            (type) => type && type.toString().toLowerCase() === businessType
          );
        });
      });

      console.log("Filtered orders:", filteredOrders);

      const sortedOrders = filteredOrders.sort(
        (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
      );

      setOrders(sortedOrders);
      setCustomer(customerRes.data);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to load orders");
    }
  };

  const handleApproveChange = async (orderId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to confirm this order?"
    );
    if (!isConfirmed) return;

    setLoadingId(orderId);

    try {
      const newStatus = { newStatus: "approved" };
      const res = await Axios.put(`/order/changestatus/${orderId}`, newStatus);

      // Update the local state to reflect the change
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, orderStatus: "approved" } : order
        )
      );

      toast.success("Order status updated successfully!");
    } catch (error) {
      console.error("Error changing order status:", error);
      toast.error("Failed to update order status.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleDownloadPDF = (order) => {
    const element = poRef.current;
    const opt = {
      margin: 0.5,
      filename: `purchase_order_${order._id}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    // Create a temporary div with the PDF content
    const pdfContent = document.createElement("div");
    pdfContent.innerHTML = generatePDFContent(order);

    html2pdf().from(pdfContent).set(opt).save();
  };
  const generatePDFContent = (order) => {
    return `
      <div style="padding: 20px; border: 1px solid #ccc; background: white;">
       <div style="text-align: center; margin-bottom: 20px;">
        <img src="/images/logo.svg" alt="Company Logo" style="height: 40px;" />
        <br />
        <h3 style="text-align:center; margin-bottom:20px;">PURCHASE ORDER</h3>
  
        <div style="display:flex; justify-content:space-between; margin-bottom:20px;">
          <div><strong>REFERENCE:</strong> ${order.orderNumber}</div>
          <div><strong>ORDER DATE:</strong> ${new Date(
            order.orderDate
          ).toLocaleDateString()}</div>
        </div>
  
        <div style="display:flex; justify-content:space-between; margin-bottom:20px;">
          <div>
            <h5>Customer / Bill To:</h5>
             ${
               customer?.photo?.url
                 ? `<img src="${customer.photo.url}" alt="Customer" style="width: 100px; height: 100px; object-fit: cover; border-radius: 50%; border: 2px solid #ccc; margin-bottom: 10px;" />`
                 : ""
             }
            <p>
              ${customer.customerName || "N/A"}<br />
              ${customer.address || "N/A"}<br />
              Phone: ${customer.phoneNumber || "N/A"}<br />
              Email: ${customer.email || "N/A"}
            </p>
          </div>
          <div>
            <h5>Deliver To:</h5>
            <p>
              ${customer.customerName || "N/A"}<br />
              ${customer.address || "N/A"}<br />
              Phone: ${customer.phoneNumber || "N/A"}<br />
              Email: ${customer.customerEmail || "N/A"}
            </p>
          </div>
        </div>
  
    
  
        <div style="margin-bottom:20px;">
          <h5>Product Details:</h5>
          <table style="width:100%; border-collapse:collapse; text-align:center;">
            <thead>
              <tr style="background-color:#f2f2f2;">
                <th style="border:1px solid #ccc; padding:8px;">#</th>
                <th style="border:1px solid #ccc; padding:8px;">Product Name</th>
                <th style="border:1px solid #ccc; padding:8px;">Quantity</th>
                <th style="border:1px solid #ccc; padding:8px;">Unit Price (${
                  order.currency
                })</th>
                <th style="border:1px solid #ccc; padding:8px;">Amount (${
                  order.currency
                })</th>
              </tr>
            </thead>
            <tbody>
              ${order.orderItems
                .map(
                  (item, index) => `
                <tr>
                  <td style="border:1px solid #ccc; padding:8px;">${
                    index + 1
                  }</td>
                  <td style="border:1px solid #ccc; padding:8px;">${
                    item.productName
                  }</td>
                  <td style="border:1px solid #ccc; padding:8px;">${
                    item.quantity
                  }</td>
                  <td style="border:1px solid #ccc; padding:8px;">${
                    item.quantity
                  }</td>
                  <td style="border:1px solid #ccc; padding:8px;">${
                    item.customerTotalPerItem
                  }</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </div>
  
     <div style="text-align:right; margin-top:20px;">
  <p><strong>Subtotal (${order.currency}):</strong> ${order.orderItems
      .reduce((sum, item) => sum + (item.customerTotalPerItem || 0), 0)
      .toFixed(2)}</p>
  <p><strong>Sales Tax (${order.tax}%):</strong></p>
   <p><strong>Total Amount (${
     order.currency
   }):</strong> ${order.customerTotal?.toFixed(2)}</p>
</div>

  
        <div style="margin-top:40px;">
          <p><strong>Approved By:</strong> ${
            order.approvedBy || "Authorized Signatory"
          }</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>Authorized Signatory</strong></p>
        </div>
  
        <div style="margin-top:30px; font-size:12px; text-align:center; color:gray;">
          This purchase order is considered accepted by you in all respects if no objection is raised within 3 days from the order date.<br/>
          <br/>
          With best regards,<br/>
          For ${order.supplierName || "Asia Part Souq"}
        </div>
      </div>
    `;
  };

  const calculateSubtotal = (orderItems) => {
    return orderItems.reduce(
      (sum, item) => sum + (item.customerTotalPerItem || 0),
      0
    );
  };

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundImage: "url(/images/bnr-bg.png)",
  };

  return (
    <div style={sectionStyle}>
      {/* <Header /> */}
      <div className="Main-div">
        <TopNavBar />
        <div className="TopSearchBar">
          <TopSearchBar />
        </div>
        <section class="purchaseHistory" className="order-section-main">
          <div class="container">
            <div class="mt-3 result-select">
              <div className="col-lg-12 purchase-spacing">
                <p class="movePages">
                  {" "}
                  <img src="/images/backIcon.svg" />{" "}
                  <Link to="/account" class="account">
                    {" "}
                    Account{" "}
                  </Link>{" "}
                  <a href="#"> Purchase order </a>{" "}
                </p>
                <div class="row no-gutters purchaseHisOptions">
                  <div class="col-lg-6">
                    <h4 class="text-uppercase"> Purchase Order</h4>
                  </div>
                  <div class="col-lg-6">
                    <div class="form-row order-search">
                      <div class="col-lg-3 my-auto column-4 col-12 offset-lg-4">
                        {/* <a href="soa.html" class="a-action"><span>SOA</span></a> */}
                      </div>
                      <div class="col-lg-2 my-auto column-3 col-6">
                        {/* <a href="#">Filter &nbsp;&nbsp;<img src="/images/filter.png"/> </a> */}
                      </div>
                      <div class="col-lg-3 my-auto column-4 col-6">
                        <a
                          href="#"
                          class="export"
                          style={{ textDecoration: "none" }}
                        >
                          <span>
                            {/* Export as PDF &nbsp;&nbsp; */}
                            <img src="/images/pdf.png" />
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <div key={order._id}>
                      <div className="historyHeading">
                        <div className="row">
                          <div className="col-md-4">
                            <p>
                              Purchase ID: <b>#{order._id}</b>
                            </p>
                          </div>
                          <div className="row align-items-center mb-3">
                            <div className="col-md-6">
                              <p className="orderDate">
                                Ordered on{" "}
                                {new Date(order.orderDate).toDateString()}
                              </p>
                            </div>
                            <div className="col-md-6 text-end">
                              {/* PDF Icon */}
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDownloadPDF(order)}
                              >
                                <i className="fas fa-file-pdf"></i> Download PDF
                              </button>
                            </div>
                          </div>

                          {/* <div className="col-md-3">
         
                               {order.orderStatus !== "approved" && (
                    <button 
                      className="btn btn-success"
                      onClick={() => handleApproveChange(order._id)}
                      disabled={loadingId === order._id}
                    >
                      {loadingId === order._id ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        "Confirm"
                      )}
                    </button>
                  )}
          </div> */}
                        </div>
                      </div>
                      <div className="historyContent">
                        <div className="row">
                          <div className="col-md-6">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th scope="col">Items</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.orderItems.map((item) => (
                                  <tr key={item._id}>
                                    <td>{toTitleCase(item.productName)}</td>
                                    <td>Qty: {item.quantity}</td>
                                    <td>
                                      {order.currency}{" "}
                                      {item.customerTotalPerItem}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="col-md-5 orderSummary">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th scope="col">Order summary</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const calculatedSubTotal =
                                    order.orderItems.reduce(
                                      (acc, item) =>
                                        acc + (item.customerTotalPerItem || 0),
                                      0
                                    );
                                  return (
                                    <tr>
                                      <td>Sub total</td>
                                      <td>
                                        {order.currency}{" "}
                                        {calculatedSubTotal.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })()}

                                <tr>
                                  <td>vat</td>
                                  <td>{order.tax} %</td>
                                </tr>

                                <tr>
                                  <td>Payment Status</td>
                                  <td>{order.paymentStatus}</td>
                                </tr>
                                <tr>
                                  <td>Total</td>
                                  <td>
                                    {order.currency}{" "}
                                    {order.customerTotal.toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-md-8 deliveryAddress">
                          <h6>Delivery address</h6>
                          <p>
                            {toTitleCase(order.customerName)}
                            <br />
                            {order.email}
                            <br />
                            {order.phoneNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No purchase order found.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* <Footer /> */}
    </div>
  );
}

export default PurchaseOrder;
